import { Button, FooterBanner, showToast } from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import ManageAssetCustomViewForm, {
    AssetCustomViewFormValues,
} from "components/asset-management-system/custom-views/ManageAssetCustomViewForm";
import { subpageContext } from "components/layout/ApplicationLayout";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import { enableCustomAssetViewRoutes } from "components/router/Routes";
import {
    ASSET_OVERVIEW,
    AssetCustomView,
    AssetCustomViewFilterEntry,
    toFilterEntryArray,
    withDateRangeFilters,
} from "domain/asset-management/assets";
import { assetViewService } from "services/asset-management/AssetCustomViewService";
import { setViews } from "store/assetViews";
import { logger } from "utils/logging";

const FORM_ID = "createAssetViewForm";
const ASSET_VIEW_PATH = "/assets/views/";

export const findViewByName = (views: AssetCustomView[], name: string): AssetCustomView | undefined => {
    for (const view of views) {
        if (view.name === name) {
            return view;
        }
        if (view.childrenViews) {
            const found = findViewByName(view.childrenViews, name);
            if (found !== undefined) {
                return found;
            }
        }
    }
    return undefined;
};

interface Props {
    viewDetails?: AssetCustomView;
    isDuplicate?: boolean;
    parentUuid?: string;
    initialFilters?: AssetCustomViewFilterEntry[];
    dateRange?: [string, string];
    selectedAssetIds?: string[];
    folders?: AssetCustomView[];
    openFileToFilterTab?: boolean;
}

export const CreateAssetCustomViewSubpage = (props: Props) => {
    const { t } = useTranslation();
    const subpage = React.useContext(subpageContext);
    const dispatch = useDispatch();
    const isEdit = props.viewDetails !== undefined && !props.isDuplicate;
    const abortControllerRef = React.useRef<AbortController | undefined>(undefined);
    const viewDetailFilters = toFilterEntryArray(props.viewDetails?.filters);

    const [isLoading, setIsLoading] = React.useState(false);
    const [existingNames, setExistingNames] = React.useState<string[]>([]);
    const [isSubmitDisabled, setIsSubmitDisabled] = React.useState(false);

    React.useEffect(() => {
        fetchExistingViews();
    }, []);

    const fetchExistingViews = () => {
        assetViewService
            .fetchAssetRelatedViews()
            .then((response: AssetCustomView[]) => {
                setExistingNames(response.map((each) => each.name));
            })
            .catch((err: Error) => {
                logger.error("Failed to fetch asset views: ", err);
            });
    };

    const handleSubmit = async (values: AssetCustomViewFormValues) => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const filters = withDateRangeFilters(values.filters, values.dateRange?.[0], values.dateRange?.[1]);

        try {
            if (isEdit && props.viewDetails) {
                await assetViewService.editAssetCustomView(
                    props.viewDetails!.uuid,
                    {
                        uuid: props.viewDetails.uuid,
                        name: values.name,
                        folder: false,
                        columns: values.columns,
                        filters: filters,
                        parentUuid: values.parentFolder,
                        description: values.description,
                    },
                    abortController
                );
                const views = await assetViewService.fetchAssetRelatedViews();
                enableCustomAssetViewRoutes(views);
                dispatch(setViews(views));
                window.location.replace("/assets/views/" + props.viewDetails!.uuid);
                showToast(t("AssetManagement.editAssetCustomView.successMessage"), "success");
                subpage?.setSubpage({ content: undefined });
            } else {
                await assetViewService.createAssetCustomView({
                    name: values.name,
                    uuid: "",
                    folder: false,
                    columns: values.columns,
                    filters: filters,
                    parentUuid: values.parentFolder,
                    description: values.description,
                });

                showToast(t("AssetManagement.createAssetCustomView.successMessage"), "success");
                const views = await assetViewService.fetchAssetRelatedViews();
                enableCustomAssetViewRoutes(views);
                dispatch(setViews(views));
                const createdView = findViewByName(views, values.name);
                if (createdView) {
                    if (props.selectedAssetIds && props.selectedAssetIds.length > 0) {
                        await assetViewService.addAssetsToGroup(createdView.uuid, props.selectedAssetIds);
                    }
                    window.location.replace(ASSET_VIEW_PATH + createdView.uuid);
                }
                subpage?.setSubpage({ content: undefined });
            }
        } catch {
            setIsSubmitDisabled(true);
        } finally {
            setIsLoading(false);
        }
    };

    const showForm = () => {
        return (
            <ManageAssetCustomViewForm
                formId={FORM_ID}
                onSubmit={handleSubmit}
                folder={false}
                existingNames={existingNames}
                isDuplicate={props.isDuplicate}
                onSubmitDisabledChange={setIsSubmitDisabled}
                folders={props.folders}
                isFilterTabSelected={props.openFileToFilterTab}
                initialValues={
                    props.viewDetails
                        ? {
                              name: props.viewDetails.name,
                              private: false,
                              columns: props.viewDetails.columns,
                              filters: viewDetailFilters,
                              parentFolder: props.viewDetails.parentUuid ?? undefined,
                              description: props.viewDetails.description ?? "",
                              dateRange:
                                  props.viewDetails.startDate && props.viewDetails.endDate
                                      ? [props.viewDetails.startDate, props.viewDetails.endDate]
                                      : undefined,
                          }
                        : props.initialFilters
                          ? {
                                name: "",
                                private: false,
                                columns: [...new Set(ASSET_OVERVIEW)],
                                filters: props.initialFilters,
                                parentFolder: props.parentUuid ?? undefined,
                                description: "",
                                dateRange: props.dateRange,
                            }
                          : {
                                name: "",
                                private: true,
                                columns: [...new Set(ASSET_OVERVIEW)],
                                filters: [],
                                parentFolder: props.parentUuid ?? undefined,
                                description: "",
                            }
                }
            />
        );
    };

    React.useEffect(() => () => abortControllerRef.current?.abort(), []);

    return (
        <>
            <SubpageLayout
                visible
                title={
                    isEdit
                        ? t("AssetManagement.editAssetCustomView.title", { name: props.viewDetails?.name })
                        : t("AssetManagement.createAssetGroup")
                }
                loading={isLoading}
                buttons={
                    <Button variant="Primary" type="submit" form={FORM_ID} disabled={isSubmitDisabled}>
                        {isEdit ? t("Common.save") : t("Common.create")}
                    </Button>
                }
                footerBanner={isSubmitDisabled && <FooterBanner text={t("Common.fixErrors")} />}
            >
                {showForm()}
            </SubpageLayout>
        </>
    );
};
